import React from 'react'
import { SanityImage } from '@/types'
import OptimizedImage from './OptimizedImage'
import useImageLazyLoading from '@/lib/hooks/useImageLazyLoading'

interface LazyImageProps {
  image: SanityImage | null | undefined
  alt?: string
  width?: number
  height?: number
  className?: string
  fallbackText?: string
  priority?: boolean
  fill?: boolean
  sizes?: string
  quality?: number
}

/**
 * Component that lazily loads images when they enter the viewport
 */
const LazyImage: React.FC<LazyImageProps> = ({
  image,
  alt,
  width,
  height,
  className,
  fallbackText = 'No image available',
  priority = false,
  fill = false,
  sizes,
  quality,
}) => {
  const { ref, isVisible, onLoad } = useImageLazyLoading({
    enabled: !priority, // Don't lazy load if priority is true
  })

  return (
    <div ref={ref} className={className} style={!fill && width && height ? { width, height } : {}}>
      {(isVisible || priority) && (
        <OptimizedImage
          image={image}
          alt={alt}
          width={width}
          height={height}
          fill={fill}
          fallbackText={fallbackText}
          priority={priority}
          onLoad={onLoad}
          sizes={sizes}
          quality={quality}
        />
      )}
      {!isVisible && !priority && (
        <div
          className="bg-gray-100 dark:bg-gray-800 w-full h-full flex items-center justify-center"
          style={!fill && width && height ? { width, height } : {}}
        >
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  )
}

export default LazyImage
